import { supabase } from '../lib/supabase';
import storageService from './storage';
import { calculateMortgagePayment } from '../utils/mortgageCalculations';

const propertyService = {
  /**
   * Analyze property from URL using AI
   */
  async analyzePropertyUrl(url, userId = null) {
    try {
      // Get the property data from AI (image import removed due to site blocking)
      const { data, error } = await supabase?.functions?.invoke('analyze-property-url', {
        body: { url }
      });

      if (error) {
        console.error('Analyze property URL error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to analyze property URL';
        throw new Error(errorMessage);
      }

      if (!data?.data) {
        throw new Error('No data returned from AI analysis');
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error('Analyze property URL error:', error);
      return { success: false, error: error?.message || 'Failed to analyze property URL' };
    }
  },

  /**
   * Calculate financial metrics for a property
   */
  calculateMetrics(propertyData) {
    const purchasePrice = parseFloat(propertyData?.purchasePrice) || 0;
    const downPaymentPercent = parseFloat(propertyData?.downPaymentPercent) || 0;
    const interestRate = parseFloat(propertyData?.interestRate) || 0;
    const amortizationYears = parseInt(propertyData?.amortizationPeriod) || 25;
    const renovationCost = parseFloat(propertyData?.renovationCost) || 0;
    const valueIncreasePercent = parseFloat(propertyData?.valueIncreasePercent) || 0;
    const closingCosts = parseFloat(propertyData?.closingCosts) || 0;
    const otherInitialCosts = parseFloat(propertyData?.otherInitialCosts) || 0;
    const totalMonthlyRevenue = parseFloat(propertyData?.totalMonthlyRevenue) || 0;
    const totalMonthlyExpenses = parseFloat(propertyData?.totalMonthlyExpenses) || 0;

    // Calculate financing values
    const downPaymentAmount = purchasePrice * (downPaymentPercent / 100);
    const baseMortgageAmount = purchasePrice - downPaymentAmount;

    // Calculate SCHL premium if applicable (percentage of base mortgage amount)
    const schlPremiumPercent = parseFloat(propertyData?.schlPremium) || 0;
    const schlPremiumAmount = schlPremiumPercent > 0 ? (baseMortgageAmount * (schlPremiumPercent / 100)) : 0;

    // Final mortgage amount includes SCHL premium
    const mortgageAmount = baseMortgageAmount + schlPremiumAmount;

    // Use utility function for mortgage payment calculation (handles Canadian semi-annual compounding)
    const paymentFrequency = propertyData?.paymentFrequency || 'monthly';
    const mortgagePaymentCalc = calculateMortgagePayment(
      mortgageAmount,
      interestRate, // Already as percentage (e.g., 5.5)
      amortizationYears,
      paymentFrequency
    );
    const monthlyPayment = mortgagePaymentCalc.periodicPayment;

    const afterRenovationValue = purchasePrice + (renovationCost * (1 + valueIncreasePercent / 100));
    const totalInitialInvestment = downPaymentAmount + renovationCost + closingCosts + otherInitialCosts;

    // Calculate cashflow and metrics
    const monthlyCashflow = totalMonthlyRevenue - totalMonthlyExpenses - monthlyPayment;
    const annualNOI = (totalMonthlyRevenue - totalMonthlyExpenses) * 12;
    const capRate = purchasePrice > 0 ? (annualNOI / purchasePrice) * 100 : 0;

    return {
      downPaymentAmount,
      mortgageAmount,
      schlPremiumAmount,
      monthlyPayment,
      afterRenovationValue,
      totalInitialInvestment,
      monthlyCashflow,
      capRate,
      noi: annualNOI
    };
  },

  /**
   * Convert form data to database format (camelCase to snake_case)
   */
  toDbFormat(formData, userId) {
    // Calculate totalMonthlyExpenses from detailed expenses if totalMonthlyExpenses is missing or zero
    let totalMonthlyExpenses = parseFloat(formData?.totalMonthlyExpenses) || 0;
    if (totalMonthlyExpenses === 0) {
      // Calculate from detailed expenses
      const propertyTaxes = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.propertyTaxes) || 0) / 12
        : (parseFloat(formData?.propertyTaxes) || 0);
      const insurance = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.insurance) || 0) / 12
        : (parseFloat(formData?.insurance) || 0);
      const electricity = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.electricity) || 0) / 12
        : (parseFloat(formData?.electricity) || 0);
      const heating = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.heating) || 0) / 12
        : (parseFloat(formData?.heating) || 0);
      const water = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.water) || 0) / 12
        : (parseFloat(formData?.water) || 0);
      const maintenance = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.maintenance) || 0) / 12
        : (parseFloat(formData?.maintenance) || 0);
      const otherExpenses = formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.otherExpenses) || 0) / 12
        : (parseFloat(formData?.otherExpenses) || 0);

      // Calculate management fee from revenue
      const totalMonthlyRevenue = formData?.revenueInputType === 'annual_total' && formData?.annualTotalRevenue
        ? (parseFloat(formData.annualTotalRevenue) || 0) / 12
        : formData?.unitRevenues?.length > 0
          ? formData.unitRevenues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
          : (parseFloat(formData?.totalMonthlyRevenue) || 0);

      const managementFee = totalMonthlyRevenue > 0 && formData?.managementFeePercent
        ? (totalMonthlyRevenue * (parseFloat(formData.managementFeePercent) || 0) / 100)
        : 0;

      totalMonthlyExpenses = propertyTaxes + insurance + electricity + heating + water + maintenance + otherExpenses + managementFee;
    }

    // Create a modified formData with calculated totalMonthlyExpenses for calculateMetrics
    const formDataForMetrics = {
      ...formData,
      totalMonthlyExpenses
    };

    const calculated = this.calculateMetrics(formDataForMetrics);

    return {
      user_id: userId,
      address: formData?.address || '',
      city: formData?.city || '',
      province: formData?.province || '',
      postal_code: formData?.postalCode || '',
      property_type: formData?.propertyType || 'other',
      units: parseInt(formData?.units) || 1,
      square_feet: parseInt(formData?.squareFeet) || null,
      year_built: parseInt(formData?.yearBuilt) || null,
      total_bedrooms: parseInt(formData?.totalBedrooms) || null,
      notes: formData?.notes || '',
      status: 'analysis',
      stage: formData?.stage || 'analysis',
      property_url: formData?.propertyUrl || '',

      purchase_price: parseFloat(formData?.purchasePrice) || 0,
      down_payment_percent: parseFloat(formData?.downPaymentPercent) || 0,
      down_payment_amount: calculated?.downPaymentAmount,
      mortgage_amount: calculated?.mortgageAmount,
      interest_rate: parseFloat(formData?.interestRate) || 0,
      amortization_period: parseInt(formData?.amortizationPeriod) || 25,
      payment_frequency: formData?.paymentFrequency || 'monthly',
      monthly_payment: calculated?.monthlyPayment,
      schl_premium: parseFloat(formData?.schlPremium) || 0,

      renovation_cost: parseFloat(formData?.renovationCost) || 0,
      value_increase_percent: parseFloat(formData?.valueIncreasePercent) || 0,
      closing_costs: parseFloat(formData?.closingCosts) || 0,
      other_initial_costs: parseFloat(formData?.otherInitialCosts) || 0,
      total_initial_investment: calculated?.totalInitialInvestment,
      after_renovation_value: calculated?.afterRenovationValue,

      // Calculate total_monthly_revenue from annualTotalRevenue, unitRevenues, or totalMonthlyRevenue
      total_monthly_revenue: formData?.revenueInputType === 'annual_total' && formData?.annualTotalRevenue
        ? (parseFloat(formData.annualTotalRevenue) || 0) / 12
        : formData?.unitRevenues?.length > 0
          ? formData.unitRevenues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
          : (parseFloat(formData?.totalMonthlyRevenue) || 0),

      // Convert expenses to monthly if they are annual (always store as monthly in DB)
      property_taxes: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.propertyTaxes) || 0) / 12
        : (parseFloat(formData?.propertyTaxes) || 0),
      insurance: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.insurance) || 0) / 12
        : (parseFloat(formData?.insurance) || 0),
      electricity: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.electricity) || 0) / 12
        : (parseFloat(formData?.electricity) || 0),
      heating: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.heating) || 0) / 12
        : (parseFloat(formData?.heating) || 0),
      water: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.water) || 0) / 12
        : (parseFloat(formData?.water) || 0),
      maintenance: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.maintenance) || 0) / 12
        : (parseFloat(formData?.maintenance) || 0),
      management_fee_percent: parseFloat(formData?.managementFeePercent) || 0,
      other_expenses: formData?.expensesFrequency === 'annual'
        ? (parseFloat(formData?.otherExpenses) || 0) / 12
        : (parseFloat(formData?.otherExpenses) || 0),

      // Use the calculated totalMonthlyExpenses
      total_monthly_expenses: totalMonthlyExpenses,

      vacancy_rate: parseFloat(formData?.vacancyRate) || 5,
      collection_rate: parseFloat(formData?.collectionRate) || 95,
      rent_growth_rate: parseFloat(formData?.rentGrowthRate) || 2,
      appreciation_rate: parseFloat(formData?.appreciationRate) || 3,

      monthly_cashflow: calculated?.monthlyCashflow,
      cap_rate: calculated?.capRate,
      noi: calculated?.noi,

      analysis_date: new Date()?.toISOString()
    };
  },

  /**
   * Convert database format to form data (snake_case to camelCase)
   */
  toFormFormat(dbData) {
    return {
      id: dbData?.id,
      address: dbData?.address || '',
      city: dbData?.city || '',
      province: dbData?.province || '',
      postalCode: dbData?.postal_code || '',
      propertyType: dbData?.property_type || '',
      units: dbData?.units?.toString() || '',
      squareFeet: dbData?.square_feet?.toString() || '',
      yearBuilt: dbData?.year_built?.toString() || '',
      totalBedrooms: dbData?.total_bedrooms?.toString() || '',
      notes: dbData?.notes || '',
      status: dbData?.status || 'draft',
      stage: dbData?.stage || 'analysis',
      propertyUrl: dbData?.property_url || '',

      purchasePrice: dbData?.purchase_price?.toString() || '',
      downPaymentPercent: dbData?.down_payment_percent?.toString() || '',
      interestRate: dbData?.interest_rate?.toString() || '',
      amortizationPeriod: dbData?.amortization_period?.toString() || '',
      paymentFrequency: dbData?.payment_frequency || 'monthly',
      schlPremium: dbData?.schl_premium?.toString() || '',

      renovationCost: dbData?.renovation_cost?.toString() || '',
      valueIncreasePercent: dbData?.value_increase_percent?.toString() || '',
      closingCosts: dbData?.closing_costs?.toString() || '',
      otherInitialCosts: dbData?.other_initial_costs?.toString() || '',

      totalMonthlyRevenue: dbData?.total_monthly_revenue?.toString() || '0',
      totalMonthlyExpenses: dbData?.total_monthly_expenses?.toString() || '0',

      expensesFrequency: 'monthly', // Always display as monthly (stored as monthly in DB)
      propertyTaxes: dbData?.property_taxes?.toString() || '',
      insurance: dbData?.insurance?.toString() || '',
      electricity: dbData?.electricity?.toString() || '',
      heating: dbData?.heating?.toString() || '',
      water: dbData?.water?.toString() || '',
      maintenance: dbData?.maintenance?.toString() || '',
      managementFeePercent: dbData?.management_fee_percent?.toString() || '',
      otherExpenses: dbData?.other_expenses?.toString() || '',

      vacancyRate: dbData?.vacancy_rate?.toString() || '',
      collectionRate: dbData?.collection_rate?.toString() || '',
      rentGrowthRate: dbData?.rent_growth_rate?.toString() || '',
      appreciationRate: dbData?.appreciation_rate?.toString() || '',

      photos: [],
      // Load unit revenues if they exist
      unitRevenues: dbData?.unit_revenues && Array.isArray(dbData.unit_revenues) && dbData.unit_revenues.length > 0
        ? dbData.unit_revenues.map(unit => unit?.monthly_rent?.toString() || '')
        : []
    };
  },

  /**
   * Create a new property
   */
  async create(formData, userId) {
    try {
      const propertyData = this.toDbFormat(formData, userId);
      console.log('[propertyService.update] mainImageUrl:', formData?.mainImageUrl);
      console.log('[propertyService.update] importImageUrl:', formData?.importImageUrl);
      console.log('[propertyService.update] urlToImport:', formData?.mainImageUrl || formData?.importImageUrl || null);
      
      const { data, error } = await supabase?.from('properties')?.insert(propertyData)?.select()?.single();
      if (error) throw error;

      // Save photos if any (manual uploads)
      if (formData?.photos?.length > 0) {
        await this.savePhotos(data?.id, formData?.photos);
      }

      // ✅ NEW: If you have a URL image (from Centris/AI) and want to store it like a manual upload
      // Convention: formData.mainImageUrl (or formData.importImageUrl) can be passed by your UI
      const urlToImport = formData?.mainImageUrl || formData?.importImageUrl || null;
      if (urlToImport) {
        await this.addPhotoFromUrl(data?.id, userId, urlToImport, {
          alt: formData?.mainImageAlt || 'Photo importée',
          makePrimary: true,
          folder: 'properties',
          edgeFunctionName: 'image-proxy'
        });
      }

      // Save unit revenues if any
      if (formData?.unitRevenues?.length > 0) {
        await this.saveUnitRevenues(data?.id, formData?.unitRevenues);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating property:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Save property photos
   */
  async savePhotos(propertyId, photos) {
    try {
      // Delete existing photo records first to avoid duplication
      await supabase?.from('property_photos')?.delete()?.eq('property_id', propertyId);

      if (!photos || photos.length === 0) return { success: true };

      const photoRecords = photos?.map((photo, index) => ({
        property_id: propertyId,
        file_path: photo?.filePath || '',
        url: photo?.url || '',
        alt_text: photo?.alt || '',
        is_primary: index === 0,
        display_order: index
      }));

      const { error } = await supabase?.from('property_photos')?.insert(photoRecords);
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error saving photos:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * ✅ NEW: Add a photo to a property from a remote URL using your Edge Function proxy,
   * then store it in Supabase Storage as if it was manually uploaded.
   *
   * @param {string} propertyId
   * @param {string} userId
   * @param {string} imageUrl
   * @param {object} opts { alt, makePrimary, folder, edgeFunctionName, minBytes }
   */
  async addPhotoFromUrl(propertyId, userId, imageUrl, opts = {}) {
    try {
      console.log('[propertyService.addPhotoFromUrl] propertyId:', propertyId);
      console.log('[propertyService.addPhotoFromUrl] userId:', userId);
      console.log('[propertyService.addPhotoFromUrl] imageUrl:', imageUrl);
      console.log('[propertyService.addPhotoFromUrl] edgeFunctionName:', opts?.edgeFunctionName || 'image-proxy');

      if (!propertyId || !userId || !imageUrl) {
        return { success: false, error: 'propertyId, userId and imageUrl are required' };
      }

      const {
        alt = 'Photo importée',
        makePrimary = true,
        folder = 'properties',
        edgeFunctionName = 'image-proxy',
        minBytes = 200
      } = opts;

      console.log('[propertyService.addPhotoFromUrl] calling storageService.uploadImageFromEdgeProxy...');

      // 1) Upload via storage service (proxy fetch -> File -> uploadImage)
      const uploadRes = await storageService.uploadImageFromEdgeProxy(
        imageUrl,
        userId,
        folder,
        { edgeFunctionName, minBytes }
      );

      if (!uploadRes?.success || !uploadRes?.filePath) {
        throw new Error(uploadRes?.error || 'Failed to upload image from proxy');
      }

      // 2) Get a signed URL for display
      const signed = await storageService.getSignedUrl(uploadRes.filePath);
      const photoObj = {
        filePath: uploadRes.filePath,
        url: signed?.success ? signed.url : '',
        alt
      };

      // 3) Merge with existing photos (so it behaves like adding a manual upload)
      const { data: existingRows, error: existingErr } = await supabase
        .from('property_photos')
        .select('file_path, url, alt_text, is_primary, display_order')
        .eq('property_id', propertyId)
        .order('display_order', { ascending: true });

      if (existingErr) {
        // If we can't read existing, just save this one as primary
        await this.savePhotos(propertyId, [photoObj]);
        return { success: true, data: photoObj, note: 'Could not read existing photos; replaced with new photo.' };
      }

      const existingPhotos = (existingRows || []).map((p) => ({
        filePath: p.file_path || '',
        url: p.url || '',
        alt: p.alt_text || ''
      }));

      const merged = makePrimary
        ? [photoObj, ...existingPhotos]
        : [...existingPhotos, photoObj];

      await this.savePhotos(propertyId, merged);

      return { success: true, data: photoObj };
    } catch (error) {
      console.error('Error addPhotoFromUrl:', error);
      return { success: false, error: error?.message || 'Failed to add photo from url' };
    }
  },

  /**
   * Save unit revenues
   */
  async saveUnitRevenues(propertyId, unitRevenues) {
    try {
      // Delete existing unit revenues for this property
      await supabase?.from('unit_revenues')?.delete()?.eq('property_id', propertyId);

      // If unitRevenues is an array of simple values (strings/numbers), convert to records
      const unitRecords = Array.isArray(unitRevenues) && unitRevenues.length > 0
        ? unitRevenues.map((revenue, index) => ({
          property_id: propertyId,
          unit_number: `Unit ${index + 1}`,
          monthly_rent: parseFloat(revenue) || 0
        }))
        : [];

      if (unitRecords.length > 0) {
        const { error } = await supabase?.from('unit_revenues')?.insert(unitRecords);
        if (error) throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving unit revenues:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get shared property by share_token (same row, same shape as getById).
   * Used by the visitor page so it displays the exact same data as the owner view.
   * No duplication: reads the property row directly from properties table.
   */
  async getByShareToken(shareToken) {
    if (!shareToken) {
      return { success: false, error: 'Token de partage manquant' };
    }
    try {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          property_photos (*),
          unit_revenues (*)
        `)
        .eq('share_token', shareToken)
        .eq('is_shared', true)
        .maybeSingle();

      if (error) throw error;

      // Fallback: old format ?id=property-uuid (same row, is_shared must be true)
      let resolved = data;
      if (!resolved) {
        const { data: byId, error: idError } = await supabase
          .from('properties')
          .select(`
            *,
            property_photos (*),
            unit_revenues (*)
          `)
          .eq('id', shareToken)
          .eq('is_shared', true)
          .maybeSingle();
        if (idError || !byId) {
          return { success: false, error: 'Analyse non trouvée ou lien expiré' };
        }
        resolved = byId;
      }

      // If unit_revenues missing from join (e.g. RLS), fetch explicitly for shared property
      if (!resolved.unit_revenues || resolved.unit_revenues.length === 0) {
        const { data: unitRevenues } = await supabase
          .from('unit_revenues')
          .select('*')
          .eq('property_id', resolved.id);
        if (unitRevenues?.length > 0) resolved.unit_revenues = unitRevenues;
      }

      // Same normalization as getById (use resolved so visitor gets same numbers as owner)
      if ((!resolved.total_monthly_revenue || resolved.total_monthly_revenue === 0) && resolved.unit_revenues?.length > 0) {
        resolved.total_monthly_revenue = resolved.unit_revenues.reduce((sum, unit) => {
          return sum + (parseFloat(unit.monthly_rent) || 0);
        }, 0);
      }

      if (!resolved.total_monthly_expenses || resolved.total_monthly_expenses === 0) {
        const baseExpenses =
          (parseFloat(resolved.property_taxes) || 0) +
          (parseFloat(resolved.insurance) || 0) +
          (parseFloat(resolved.electricity) || 0) +
          (parseFloat(resolved.heating) || 0) +
          (parseFloat(resolved.water) || 0) +
          (parseFloat(resolved.maintenance) || 0) +
          (parseFloat(resolved.other_expenses) || 0);

        const managementFee = resolved.total_monthly_revenue > 0 && resolved.management_fee_percent > 0
          ? (resolved.total_monthly_revenue * (parseFloat(resolved.management_fee_percent) || 0) / 100)
          : 0;

        resolved.total_monthly_expenses = baseExpenses + managementFee;
      }

      // Recompute monthly_cashflow, cap_rate, noi from raw fields (same formula as at save)
      const forMetrics = {
        purchasePrice: resolved.purchase_price,
        downPaymentPercent: resolved.down_payment_percent,
        interestRate: resolved.interest_rate,
        amortizationPeriod: resolved.amortization_period,
        paymentFrequency: resolved.payment_frequency || 'monthly',
        schlPremium: resolved.schl_premium,
        renovationCost: resolved.renovation_cost,
        valueIncreasePercent: resolved.value_increase_percent,
        closingCosts: resolved.closing_costs,
        otherInitialCosts: resolved.other_initial_costs,
        totalMonthlyRevenue: resolved.total_monthly_revenue,
        totalMonthlyExpenses: resolved.total_monthly_expenses
      };
      const metrics = this.calculateMetrics(forMetrics);
      resolved.monthly_cashflow = metrics.monthlyCashflow;
      resolved.cap_rate = metrics.capRate;
      resolved.noi = metrics.noi;
      if (metrics.downPaymentAmount != null) resolved.down_payment_amount = metrics.downPaymentAmount;
      if (metrics.mortgageAmount != null) resolved.mortgage_amount = metrics.mortgageAmount;
      if (metrics.totalInitialInvestment != null) resolved.total_initial_investment = metrics.totalInitialInvestment;

      // Load latest full AI analysis for shared view (exclude stress_tests / scenario_comparison)
      const { data: aiRows } = await supabase
        .from('property_ai_analyses')
        .select('analysis_data')
        .eq('property_id', resolved.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(20);
      const fullAi = (aiRows || []).find((r) => this._isFullAnalysis(r?.analysis_data));
      resolved.ai_analysis_data = fullAi?.analysis_data ?? null;

      return { success: true, data: resolved };
    } catch (error) {
      console.error('Error fetching shared property:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement' };
    }
  },

  /**
   * Duplicate a shared property into the current user's account (property + scenarios + AI analysis).
   * @param {Object} payload - { property, scenarios = [], aiAnalysisData = null }
   * @param {string} userId - Current user id
   * @returns {Promise<{ success: boolean, data?: { id: string }, error?: string }>}
   */
  async duplicateFromShare(payload, userId) {
    if (!payload?.property || !userId) {
      return { success: false, error: 'Données manquantes' };
    }
    const { property, scenarios = [], aiAnalysisData = null } = payload;
    try {
      const formData = this.toFormFormat(property);
      delete formData.id;
      formData.photos = (property.property_photos || []).map((p) => ({
        filePath: p.file_path || '',
        url: p.url || '',
        alt: p.alt_text || ''
      }));

      const createResult = await this.create(formData, userId);
      if (!createResult?.success || !createResult?.data?.id) {
        throw new Error(createResult?.error || 'Échec de la création de la propriété');
      }
      const newPropertyId = createResult.data.id;

      for (const scenario of scenarios) {
        const insertScenario = {
          user_id: userId,
          property_id: newPropertyId,
          name: scenario.name || 'Scénario',
          description: scenario.description ?? null,
          down_payment_percent: scenario.down_payment_percent ?? null,
          interest_rate: scenario.interest_rate ?? null,
          amortization_period: scenario.amortization_period ?? null,
          renovation_cost: scenario.renovation_cost ?? 0,
          value_increase_percent: scenario.value_increase_percent ?? 0,
          revenue_increase_percent: scenario.revenue_increase_percent ?? 0,
          closing_costs: scenario.closing_costs ?? null,
          other_initial_costs: scenario.other_initial_costs ?? null,
          schl_premium: scenario.schl_premium ?? null,
          monthly_cashflow: scenario.monthly_cashflow ?? null,
          annual_cashflow: scenario.annual_cashflow ?? null,
          cap_rate: scenario.cap_rate ?? null,
          noi: scenario.noi ?? null,
          cash_on_cash_return: scenario.cash_on_cash_return ?? null,
          roi_10_year: scenario.roi_10_year ?? null,
          total_initial_investment: scenario.total_initial_investment ?? null,
          is_baseline: scenario.is_baseline ?? false,
          down_payment_amount: scenario.down_payment_amount ?? null,
          mortgage_amount: scenario.mortgage_amount ?? null,
          monthly_payment: scenario.monthly_payment ?? null,
          dscr: scenario.dscr ?? null
        };
        const { error: scenarioError } = await supabase
          .from('property_scenarios')
          .insert(insertScenario);
        if (scenarioError) {
          console.warn('Duplicate scenario skip:', scenarioError);
        }
      }

      if (aiAnalysisData && typeof aiAnalysisData === 'object') {
        const { error: aiError } = await supabase.from('property_ai_analyses').insert({
          property_id: newPropertyId,
          user_id: userId,
          analysis_data: aiAnalysisData
        });
        if (aiError) {
          console.warn('Duplicate AI analysis skip:', aiError);
        }
      }

      return { success: true, data: { id: newPropertyId } };
    } catch (error) {
      console.error('Error duplicating shared property:', error);
      return { success: false, error: error?.message || 'Erreur lors de l\'ajout à vos propriétés' };
    }
  },

  /**
   * Get property by ID with photos
   */
  async getById(propertyId) {
    try {
      const { data, error } = await supabase?.from('properties')?.select(`
          *,
          property_photos (*),
          unit_revenues (*)
        `)?.eq('id', propertyId)?.single();

      if (error) throw error;

      // Calculate total_monthly_revenue from unit_revenues if total is missing or zero
      if ((!data.total_monthly_revenue || data.total_monthly_revenue === 0) && data.unit_revenues?.length > 0) {
        data.total_monthly_revenue = data.unit_revenues.reduce((sum, unit) => {
          return sum + (parseFloat(unit.monthly_rent) || 0);
        }, 0);
      }

      // Calculate total_monthly_expenses from detailed expenses if total is missing or zero
      if ((!data.total_monthly_expenses || data.total_monthly_expenses === 0)) {
        const baseExpenses =
          (parseFloat(data.property_taxes) || 0) +
          (parseFloat(data.insurance) || 0) +
          (parseFloat(data.electricity) || 0) +
          (parseFloat(data.heating) || 0) +
          (parseFloat(data.water) || 0) +
          (parseFloat(data.maintenance) || 0) +
          (parseFloat(data.other_expenses) || 0);

        // Add management fee if applicable
        const managementFee = data.total_monthly_revenue > 0 && data.management_fee_percent > 0
          ? (data.total_monthly_revenue * (parseFloat(data.management_fee_percent) || 0) / 100)
          : 0;

        data.total_monthly_expenses = baseExpenses + managementFee;
      }

      // Get signed URLs for photos
      if (data?.property_photos?.length > 0) {
        const photosWithUrls = await Promise.all(
          data?.property_photos?.map(async (photo) => {
            if (photo?.file_path) {
              const urlResult = await storageService?.getSignedUrl(photo?.file_path);
              return {
                url: urlResult?.success ? urlResult?.url : photo?.url,
                filePath: photo?.file_path,
                alt: photo?.alt_text || '',
                // ✅ Keep flags so UI can detect primary properly
                is_primary: !!photo?.is_primary,
                display_order: photo?.display_order ?? 0
              };
            }
            return {
              url: photo?.url,
              filePath: photo?.file_path,
              alt: photo?.alt_text || '',
              is_primary: !!photo?.is_primary,
              display_order: photo?.display_order ?? 0
            };
          })
        );

        data.photos = photosWithUrls;

        // Set mainImage from primary photo or first photo
        const primaryPhoto = photosWithUrls?.find(p => p?.is_primary) || photosWithUrls?.[0];
        if (primaryPhoto) {
          data.mainImage = primaryPhoto?.url;
          data.mainImageAlt = primaryPhoto?.alt || '';
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching property:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get all properties for a user
   */
  async getUserProperties(userId) {
    try {
      const { data, error } = await supabase?.from('properties')?.select(`
          *,
          property_photos!property_photos_property_id_fkey (
            id,
            file_path,
            url,
            alt_text,
            is_primary
          )
        `)?.eq('user_id', userId)?.is('deleted_at', null)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get signed URLs for primary photos and load AI analyses
      const propertiesWithPhotos = await Promise.all(
        data?.map(async (property) => {
          const primaryPhoto = property?.property_photos?.find(p => p?.is_primary) || property?.property_photos?.[0];

          if (primaryPhoto?.file_path) {
            const urlResult = await storageService?.getSignedUrl(primaryPhoto?.file_path);
            property.mainImage = urlResult?.success ? urlResult?.url : primaryPhoto?.url;
            property.mainImageAlt = primaryPhoto?.alt_text || '';
          }

          // Load AI analysis if it exists
          try {
            const aiAnalysisResult = await this.getPropertyAIAnalysis(property.id, userId);
            if (aiAnalysisResult?.success && aiAnalysisResult?.data) {
              property.aiAnalysis = aiAnalysisResult.data;
            }
          } catch (err) {
            // Silently fail if AI analysis doesn't exist or table doesn't exist
            console.debug('No AI analysis found for property:', property.id);
          }

          return property;
        })
      );

      return { success: true, data: propertiesWithPhotos };
    } catch (error) {
      console.error('Error fetching user properties:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Alias for getUserProperties
   */
  async getAll(userId) {
    return this.getUserProperties(userId);
  },

  /**
   * Update property
   */
  async update(propertyId, formData, userId) {
    try {
      const propertyData = this.toDbFormat(formData, userId);
      
      console.log('[propertyService.create] mainImageUrl:', formData?.mainImageUrl);
      console.log('[propertyService.create] importImageUrl:', formData?.importImageUrl);
      console.log('[propertyService.create] urlToImport:', formData?.mainImageUrl || formData?.importImageUrl || null);
      
      const { data, error } = await supabase
        ?.from('properties')
        ?.update(propertyData)
        ?.eq('id', propertyId)
        ?.eq('user_id', userId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Update photos if provided (manual uploads)
      if (formData?.photos) {
        await this.savePhotos(propertyId, formData.photos);
      }

      // ✅ NEW: Import from URL if requested during update too
      const urlToImport = formData?.mainImageUrl || formData?.importImageUrl || null;
      if (urlToImport) {
        await this.addPhotoFromUrl(propertyId, userId, urlToImport, {
          alt: formData?.mainImageAlt || 'Photo importée',
          makePrimary: true,
          folder: 'properties',
          edgeFunctionName: 'image-proxy'
        });
      }


      // Update unit revenues if provided
      if (formData?.unitRevenues) {
        await this.saveUnitRevenues(propertyId, formData.unitRevenues);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error updating property:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Delete property
   */
  async delete(propertyId, userId) {
    try {
      // Get property photos to delete from storage
      const { data: photos } = await supabase?.from('property_photos')?.select('file_path')?.eq('property_id', propertyId);

      // Soft delete property (update deleted_at)
      const { error } = await supabase
        ?.from('properties')
        ?.update({ deleted_at: new Date().toISOString() })
        ?.eq('id', propertyId)
        ?.eq('user_id', userId);

      if (error) throw error;

      // Delete photos from storage
      if (photos?.length > 0) {
        for (const photo of photos) {
          if (photo?.file_path) {
            await storageService?.deleteImage(photo?.file_path);
          }
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting property:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Types in property_ai_analyses that are NOT the "full" property AI analysis
   * (stress tests and scenario comparison are stored in the same table but separate)
   */
  _otherAnalysisTypes: ['stress_tests', 'scenario_comparison'],

  _isFullAnalysis(analysisData) {
    const type = analysisData?.type;
    return !type || !this._otherAnalysisTypes.includes(type);
  },

  /**
   * Get saved AI analysis for a property (full analysis only; excludes stress_tests and scenario_comparison)
   */
  async getPropertyAIAnalysis(propertyId, userId) {
    try {
      if (!propertyId || !userId) {
        return { success: false, error: 'Property ID and User ID are required' };
      }

      const { data: rows, error } = await supabase
        .from('property_ai_analyses')
        .select('analysis_data')
        .eq('property_id', propertyId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        return { success: true, data: null };
      }

      const full = (rows || []).find((r) => this._isFullAnalysis(r?.analysis_data));
      return { success: true, data: full?.analysis_data || null };
    } catch (error) {
      return { success: true, data: null };
    }
  },

  /**
   * Save AI analysis for a property
   */
  async savePropertyAIAnalysis(propertyId, userId, analysisData, forceNew = false) {
    try {
      if (!propertyId || !userId || !analysisData) {
        return { success: false, error: 'Property ID, User ID, and analysis data are required' };
      }

      // If forceNew is true, always create a new entry (for regeneration - counts as new usage)
      if (forceNew) {
        console.log('[savePropertyAIAnalysis] Creating new entry (forceNew=true) for property:', propertyId, 'user:', userId);
        const { data, error } = await supabase
          .from('property_ai_analyses')
          .insert({
            property_id: propertyId,
            user_id: userId,
            analysis_data: analysisData,
            created_at: new Date().toISOString() // Explicitly set created_at to ensure it's current
          })
          .select()
          .single();

        if (error) {
          console.error('[savePropertyAIAnalysis] Error creating new entry:', error);
          console.error('[savePropertyAIAnalysis] Error details:', JSON.stringify(error, null, 2));
          // If table doesn't exist, silently fail
          if (error.code === '42P01' || error.status === 406) {
            console.warn('Property AI analyses table does not exist yet. Please run the migration.');
            return { success: false, error: 'Table not available. Migration required.' };
          }
          // Return error instead of throwing to allow caller to handle it
          return { success: false, error: error?.message || 'Failed to save AI analysis', details: error };
        }
        console.log('[savePropertyAIAnalysis] Successfully created new entry with id:', data?.id, 'created_at:', data?.created_at);
        return { success: true, data: data?.analysis_data || analysisData };
      }

      // Check if a full analysis already exists (exclude stress_tests and scenario_comparison)
      console.log('[savePropertyAIAnalysis] Checking for existing full analysis, propertyId:', propertyId, 'userId:', userId);
      const { data: existingRows, error: checkError } = await supabase
        .from('property_ai_analyses')
        .select('id, analysis_data')
        .eq('property_id', propertyId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (checkError && (checkError.code === '42P01' || checkError.status === 406)) {
        console.warn('Property AI analyses table does not exist yet. Please run the migration.');
        return { success: false, error: 'Table not available. Migration required.' };
      }

      const existing = (existingRows || []).find((r) => this._isFullAnalysis(r?.analysis_data));
      console.log('[savePropertyAIAnalysis] Existing check result - existing:', existing ? { id: existing.id } : null, 'checkError:', checkError);

      if (existing && !checkError) {
        console.log('[savePropertyAIAnalysis] Existing full analysis found, updating id:', existing.id);
        // Update existing analysis
        const { data, error } = await supabase
          .from('property_ai_analyses')
          .update({
            analysis_data: analysisData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          console.error('[savePropertyAIAnalysis] Error updating existing entry:', error);
          console.error('[savePropertyAIAnalysis] Error details:', JSON.stringify(error, null, 2));
          // If table doesn't exist, silently fail
          if (error.code === '42P01' || error.status === 406) {
            console.warn('Property AI analyses table does not exist yet. Please run the migration.');
            return { success: false, error: 'Table not available. Migration required.' };
          }
          // Return error instead of throwing to allow caller to handle it
          return { success: false, error: error?.message || 'Failed to update AI analysis', details: error };
        }
        console.log('[savePropertyAIAnalysis] Successfully updated existing entry with id:', data?.id);
        return { success: true, data: data?.analysis_data || analysisData };
      } else {
        // Create new analysis
        console.log('[savePropertyAIAnalysis] No existing analysis found, creating new entry');
        const { data, error } = await supabase
          .from('property_ai_analyses')
          .insert({
            property_id: propertyId,
            user_id: userId,
            analysis_data: analysisData
          })
          .select()
          .single();

        if (error) {
          console.error('[savePropertyAIAnalysis] Error creating new entry (else branch):', error);
          console.error('[savePropertyAIAnalysis] Error details:', JSON.stringify(error, null, 2));
          // If table doesn't exist, silently fail
          if (error.code === '42P01' || error.status === 406) {
            console.warn('Property AI analyses table does not exist yet. Please run the migration.');
            return { success: false, error: 'Table not available. Migration required.' };
          }
          // Return error instead of throwing to allow caller to handle it
          return { success: false, error: error?.message || 'Failed to save AI analysis', details: error };
        }
        console.log('[savePropertyAIAnalysis] Successfully created new entry (else branch) with id:', data?.id);
        return { success: true, data: data?.analysis_data || analysisData };
      }
    } catch (error) {
      // If table doesn't exist, silently fail
      if (error.code === '42P01' || error.status === 406) {
        console.warn('Property AI analyses table does not exist yet. Please run the migration.');
        return { success: false, error: 'Table not available. Migration required.' };
      }
      console.error('[savePropertyAIAnalysis] Unexpected error:', error);
      console.error('[savePropertyAIAnalysis] Error details:', JSON.stringify(error, null, 2));
      return { success: false, error: error?.message || 'Failed to save AI analysis' };
    }
  },

  /**
   * Analyze property using AI to generate insights (and save it)
   * @param {Object} propertyData - Property data to analyze
   * @param {string} userId - User ID
   * @param {string} propertyId - Property ID (optional, for caching)
   * @param {boolean} forceRegenerate - Force regeneration of analysis
   * @param {Object} investorProfile - Investor profile for personalized analysis (optional)
   */
  async analyzePropertyAI(propertyData, userId = null, propertyId = null, forceRegenerate = false, investorProfile = null) {
    try {
      // First, try to load existing analysis if propertyId is provided and NOT forcing regenerate
      if (propertyId && userId && !forceRegenerate) {
        const existingResult = await this.getPropertyAIAnalysis(propertyId, userId);
        if (existingResult?.success && existingResult?.data) {
          return { success: true, data: existingResult.data, cached: true };
        }
      }

      // Generate new analysis with investor profile for personalization
      const { data, error } = await supabase?.functions?.invoke('analyze-property-ai', {
        body: { propertyData, userId, investorProfile }
      });

      if (error) {
        console.error('Analyze property AI error:', error);
        const errorMessage = error?.message || data?.error || 'Failed to analyze property with AI';
        throw new Error(errorMessage);
      }

      if (!data?.data) {
        throw new Error('No analysis data returned from AI');
      }

      // Save the analysis if propertyId is provided (always save, even on regenerate)
      let saveError = null;
      if (propertyId && userId) {
        console.log('[analyzePropertyAI] Saving analysis with forceRegenerate:', forceRegenerate, 'propertyId:', propertyId, 'userId:', userId);
        const saveResult = await this.savePropertyAIAnalysis(propertyId, userId, data.data, forceRegenerate);
        if (!saveResult.success) {
          saveError = saveResult.error;
          if (saveResult.error?.includes('Migration required')) {
            console.warn('Analysis generated but not saved. Table migration required.');
          } else {
            console.error('[analyzePropertyAI] Failed to save analysis:', saveResult.error);
          }
        } else {
          console.log('[analyzePropertyAI] Analysis saved successfully, forceRegenerate was:', forceRegenerate);
        }
      } else {
        console.warn('[analyzePropertyAI] Not saving analysis - missing propertyId or userId. propertyId:', propertyId, 'userId:', userId);
        saveError = 'Missing propertyId or userId';
      }

      return {
        success: true,
        data: data.data,
        cached: false,
        saveError: saveError
      };
    } catch (error) {
      console.error('Analyze property AI error:', error);
      return { success: false, error: error?.message || 'Failed to analyze property with AI' };
    }
  }
};

export default propertyService;
